import {
  Box,
  Dialog,
  Divider,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { generateClient } from 'aws-amplify/api';

import { Icon } from '@iconify/react';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Schema } from '../../../../amplify/data/resource';
import { FundLogo } from '../../../components/custom/FundLogo';
import CoinIcon from '../../../components/icons/CoinIcon';
import { formatMoney } from '../../../utils/format/format';

const apiClient = generateClient<Schema>();

const Link = ({
  open,
  onClose,
  paymentLink,
  amount,
  clientName,
  indexLogo,
}: {
  open: boolean;
  onClose: () => void;
  paymentLink: string;
  amount: number;
  clientName?: string;
  indexLogo: string;
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState<number | null>(null);

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    let timerId: NodeJS.Timeout;

    if (open) {
      setTimeLeft(15 * 60);

      timerId = setInterval(() => {
        setTimeLeft((prevTimeLeft) => {
          if (prevTimeLeft === null) return null;
          if (prevTimeLeft <= 1) {
            clearInterval(timerId);
            onClose();
            return 0;
          }
          return prevTimeLeft - 1;
        });
      }, 1000);
    }

    return () => {
      clearInterval(timerId);
    };
  }, [open, onClose]);

  const formatTime = (seconds: number | null) => {
    if (seconds === null) return '00:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(
      remainingSeconds,
    ).padStart(2, '0')}`;
  };

  const handleCopyClick = () => {
    navigator.clipboard
      .writeText(paymentLink)
      .then(() => {
        enqueueSnackbar('הקישור הועתק בהצלחה!', { variant: 'success' });
      })
      .catch((err) => {
        enqueueSnackbar('אירעה שגיאה בהעתקת הקישור.', { variant: 'error' });
        console.error('Failed to copy link: ', err);
      });
  };

  const shareByEmail = () => {
    const subject = encodeURIComponent('לינק לתשלום');
    const body = encodeURIComponent(
      `שלום,\n\nמצורף לינק לתשלום בסך ${formatMoney(amount)} ש״ח:\n${paymentLink}\n\nבברכה,\n[שם השולח]`,
    );
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  const shareBySMS = () => {
    const body = encodeURIComponent(`שלום, מצורף לינק לתשלום: ${paymentLink}`);

    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.indexOf('iphone') > -1 || userAgent.indexOf('ipad') > -1) {
      window.open(`sms:&body=${body}`);
    } else {
      window.open(`sms:?body=${body}`);
    }
  };

  const shareByWhatsApp = () => {
    const text = encodeURIComponent(
      `שלום,\n\nמצורף לינק לתשלום בסך ${formatMoney(amount)} ש״ח:\n${paymentLink}`,
    );
    window.open(`https://wa.me/?text=${text}`);
  };

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: '35px',
        },
      }}
    >
      <Stack
        alignItems="center"
        spacing={4}
        sx={{
          width: '500px',
          '& .MuiDialog-paper': {
            padding: '60px',
            boxSizing: 'border-box',
            position: 'relative',
            overflow: 'hidden',
          },
          px: '40px',
          py: '70px',
        }}
      >
        <IconButton
          onClick={() => {
            navigate('/agent-dashboard');
          }}
          sx={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            zIndex: 1,
          }}
        >
          <img
            alt="close"
            src={'/assets/client/close.svg'}
            width="15"
            height="15"
            style={{ cursor: 'pointer' }}
          />
        </IconButton>

        <Stack>
          <Typography
            textAlign="center"
            fontSize="20px"
            fontWeight="700"
            fontFamily={'AlmoniDL'}
          >
            לינק הוראת תשלום (תקף ל-15 דקות)
          </Typography>
          <Typography
            textAlign="center"
            fontSize="20px"
            fontWeight="700"
            fontFamily={'AlmoniDL'}
          >
            {clientName}ל
          </Typography>
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: '100%' }}
        >
          <Stack
            spacing={1}
            alignItems="flex-end"
            sx={{ width: '230px', pr: 2 }}
          >
            <Typography
              fontSize="14px"
              fontWeight="700"
              fontFamily={'AlmoniDL'}
            >
              :בסכום של
            </Typography>
            <Stack direction="row-reverse" alignItems="center">
              <CoinIcon />
              <Typography
                fontSize="28px"
                fontWeight="400"
                fontFamily={'Montserrat'}
              >
                {formatMoney(amount)}
              </Typography>
            </Stack>
          </Stack>

          <Divider
            orientation="vertical"
            flexItem
            sx={{ backgroundColor: theme.palette.dark?.border || 'black' }}
          />

          <Stack sx={{ width: '230px', pl: 2 }}>
            <FundLogo width="130px" logoIndex={indexLogo} />
          </Stack>
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={3}
          sx={{
            width: '100%',
            height: '45px',
            boxSizing: 'border-box',
            background: '#F2F4F7',
            borderRadius: '17px',
            p: '12px',
          }}
        >
          <Box
            sx={{
              background: '#B6F829',
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              cursor: 'pointer',
            }}
            onClick={handleCopyClick}
          >
            <Icon width="23px" icon="ep:copy-document" />
          </Box>
          <Typography
            fontSize="20px"
            fontWeight="700"
            fontFamily="AlmoniDL"
            noWrap
            sx={{
              textOverflow: 'ellipsis',
            }}
          >
            {paymentLink}
          </Typography>
          <Box sx={{ background: '#E3E4E6', borderRadius: '17px', p: '8px' }}>
            <Typography
              fontSize="18
px"
              fontWeight="300"
              fontFamily={'AlmoniDL'}
            >
              {formatTime(timeLeft)}
            </Typography>
          </Box>
        </Stack>

        <Stack alignItems="center" spacing={2}>
          <Typography fontSize="16px" fontWeight="300" fontFamily={'AlmoniDL'}>
            :שתף
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
          >
            <Stack
              spacing={1}
              direction="row"
              alignItems="center"
              sx={{
                background: '#B6F829',
                borderRadius: '16px',
                px: '16px',
                py: '8px',
              }}
              onClick={shareByEmail}
            >
              <Typography
                fontSize="16px"
                fontWeight="700"
                fontFamily={'AlmoniDL'}
              >
                מייל
              </Typography>
              <Icon icon="ic:sharp-alternate-email" />
            </Stack>
            <Stack
              spacing={1}
              direction="row"
              alignItems="center"
              sx={{
                background: '#B6F829',
                borderRadius: '16px',
                px: '16px',
                py: '8px',
              }}
              onClick={shareBySMS}
            >
              <Typography
                fontSize="16px"
                fontWeight="700"
                fontFamily={'AlmoniDL'}
              >
                SMS
              </Typography>
              <Icon icon="hugeicons:mail-02" />
            </Stack>
            <Stack
              spacing={1}
              direction="row"
              alignItems="center"
              sx={{
                background: '#B6F829',
                borderRadius: '16px',
                px: '16px',
                py: '8px',
              }}
              onClick={shareByWhatsApp}
            >
              <Typography
                fontSize="16px"
                fontWeight="700"
                fontFamily={'AlmoniDL'}
              >
                ווטסאפ
              </Typography>
              <Icon icon="ic:baseline-whatsapp" />
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Dialog>
  );
};

export default Link;
